// components/NotificationBell/CustomNotificationPopup.tsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Typography } from 'antd';
import { CheckOutlined, UserOutlined, PhoneOutlined, HomeOutlined, FileTextOutlined } from '@ant-design/icons';
import { UserInfoNotification } from '../../types/notifications';
import { createPortal } from 'react-dom';

const { Text } = Typography;

interface CustomNotificationPopupProps {
  notification: UserInfoNotification;
  onResolve: (id: string) => void;
  onClose: (id: string) => void;
  top: number;
  theme: 'dark' | 'light';
}

export const CustomNotificationPopup: React.FC<CustomNotificationPopupProps> = React.memo(({
  notification,
  onResolve,
  onClose,
  top,
  theme
}) => {
  const [visible, setVisible] = useState(false);
  const isMountedRef = useRef(false);

  useEffect(() => {
    // Solo animar la entrada al montar
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      setTimeout(() => setVisible(true), 10);
    }
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => onClose(notification.id), 300);
  }, [onClose, notification.id]);

  const handleResolve = useCallback(() => {
    setVisible(false);
    setTimeout(() => onResolve(notification.id), 300);
  }, [onResolve, notification.id]);

  const content = (
    <div
      style={{
        position: 'fixed',
        top: `${top}px`,
        right: visible ? '24px' : '-500px',
        width: '480px',
        backgroundColor: theme === 'dark' ? '#1f1f1f' : '#fff',
        borderRadius: '8px',
        boxShadow: theme === 'dark'
          ? '0 4px 12px rgba(0, 0, 0, 0.5)'
          : '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: '12px 16px',
        zIndex: 9999 - Math.floor((top - 80) / 140),
        transition: 'right 0.3s ease-in-out, top 0.3s ease-in-out',
        border: `1px solid ${theme === 'dark' ? '#333' : '#e8e8e8'}`,
        maxHeight: '130px',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        paddingBottom: '6px',
        borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#e8e8e8'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FileTextOutlined style={{ fontSize: '14px', color: '#1890ff' }} />
          <Text strong style={{ fontSize: '13px' }}>
            Nueva Solicitud
          </Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={handleResolve}
            style={{ height: '24px', fontSize: '12px' }}
          >
            Resolver
          </Button>
          <span
            onClick={handleClose}
            style={{
              cursor: 'pointer',
              fontSize: '16px',
              color: theme === 'dark' ? '#999' : '#666',
              lineHeight: 1,
              padding: '2px 6px',
              borderRadius: '4px',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme === 'dark' ? '#333' : '#f0f0f0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            ✕
          </span>
        </div>
      </div>

      {/* Content - Grid de 2 columnas */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '6px 12px',
        fontSize: '12px'
      }}>
        {notification.username && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <UserOutlined style={{ color: '#1890ff', fontSize: '12px', flexShrink: 0 }} />
            <Text style={{ fontSize: '12px' }} ellipsis={{ tooltip: notification.username }}>
              <strong>Usuario:</strong> {notification.username}
            </Text>
          </div>
        )}
        {notification.phone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <PhoneOutlined style={{ color: '#52c41a', fontSize: '12px', flexShrink: 0 }} />
            <Text style={{ fontSize: '12px' }} ellipsis={{ tooltip: notification.phone }}>
              <strong>Tel:</strong> {notification.phone}
            </Text>
          </div>
        )}
        {notification.phoneOffice && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <PhoneOutlined style={{ color: '#fa8c16', fontSize: '12px', flexShrink: 0 }} />
            <Text style={{ fontSize: '12px' }} ellipsis={{ tooltip: notification.phoneOffice }}>
              <strong>Oficina:</strong> {notification.phoneOffice}
            </Text>
          </div>
        )}
        {notification.officeName && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <HomeOutlined style={{ color: '#722ed1', fontSize: '12px', flexShrink: 0 }} />
            <Text style={{ fontSize: '12px' }} ellipsis={{ tooltip: notification.officeName }}>
              <strong>Sede:</strong> {notification.officeName}
            </Text>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}, (prevProps, nextProps) => {
  // Solo re-renderizar si cambian las props relevantes
  // Retornar true significa NO re-renderizar
  return (
    prevProps.notification.id === nextProps.notification.id &&
    prevProps.top === nextProps.top &&
    prevProps.onResolve === nextProps.onResolve &&
    prevProps.onClose === nextProps.onClose &&
    prevProps.theme === nextProps.theme
  );
});

CustomNotificationPopup.displayName = 'CustomNotificationPopup';
